import React, { useContext, useState } from "react";
import { Avatar, Card, Divider, IconButton } from "react-native-paper";
import { Text, View } from "../../shared/Themed";
import {
  StyleSheet,
  ScrollView,
  Image,
  Modal,
  TouchableOpacity,
  TouchableWithoutFeedback,
} from "react-native";
import Spinner from "../../shared/Spinner";
import { useAppTheme } from "../../../hooks/useAppTheme";

import DeviceOrientationContext from "../../context/DeviceOrientationContext";
import { ReadOnlyProps } from "../../../utils/templates";

interface Props {
  childern: React.ReactNode | JSX.Element;
  title: string;
  subTitle?: string;
  icon?: string;
  loading?: boolean;
  uri?: string;
  cardContainerHeight?: string;
  setModalVisible?: (event: any) => void;
  modalVisible?: boolean;
  width?: number | string;
  minHeight?: string;
  maxHeight?: string;
  headerRight?: React.ReactNode;
}

export const ModalContent: React.FC<ReadOnlyProps<Props>> = (
  props: ReadOnlyProps<Props>,
) => {
  const { isMediumDevice, isSmallDevice } = useContext(
    DeviceOrientationContext,
  );
  const { colors } = useAppTheme();
  const styles = createStyles(colors);
  const [isImagePreviewVisible, setIsImagePreviewVisible] = useState(false);

  const leftContent = () => {
    if (!props.icon) return null;
    return (
      <Avatar.Icon
        size={32}
        icon={props.icon}
        color={colors.primary}
        style={{ backgroundColor: colors.primaryBg }}
      />
    );
  };

  return (
    <View
      style={[
        styles.cardViewContainer,
        {
          width: isSmallDevice ? "100%" : (props.width ?? 640),
          minWidth: isSmallDevice ? "100%" : isMediumDevice ? "30%" : "24%",
          minHeight: isSmallDevice ? "75%" : (props.minHeight ?? "80%"),
          maxHeight: isSmallDevice ? "94%" : (props.maxHeight ?? "100%"),
          flex: 1,
        },
      ]}
    >
      <Card
        mode="outlined"
        theme={{ colors: { surface: colors.surface } }}
        style={[
          styles.cardContainer,
          {
            height: props.cardContainerHeight
              ? props.cardContainerHeight
              : "100%",
          },
        ]}
      >
        <Card.Title
          style={styles.cardTitle}
          title={props.title}
          titleStyle={{ color: colors.text }}
          subtitleStyle={{ color: colors.textSecondary }}
          subtitleNumberOfLines={0}
          subtitle={
            props.subTitle ? (
              <Text style={{ flex: 1, flexWrap: "wrap" }}>
                {props.subTitle}
              </Text>
            ) : null
          }
          left={leftContent}
          leftStyle={{
            display: props.icon === undefined ? "none" : "flex",
            padding: 0,
            marginRight: 0,
          }}
          right={(rightProps) => (
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                backgroundColor: "transparent",
              }}
            >
              {props.headerRight}
              {props.setModalVisible ? (
                <IconButton
                  {...rightProps}
                  icon="close"
                  color={colors.text}
                  onPress={() =>
                    props.setModalVisible && props.setModalVisible(false)
                  }
                />
              ) : null}
            </View>
          )}
        />
        <Divider />
        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ flexGrow: 1 }}>
          {props.uri !== undefined ? (
            !props.loading ? (
              <>
                <TouchableOpacity
                  activeOpacity={0.8}
                  onPress={() => props.uri && setIsImagePreviewVisible(true)}
                >
                  <Image 
                    style={styles.cardImage} 
                    source={props.uri ? { uri: props.uri } : require("../../../assets/noimage.png")} 
                  />
                </TouchableOpacity>
                <Divider />
              </>
            ) : (
              <>
                <View style={styles.spinnerContainer}>
                  <Spinner
                    size="small"
                    color={colors.primary}
                    styles={{ flexDirection: "row" }}
                  />
                </View>
                <Divider />
              </>
            )
          ) : null}
          <Card.Content style={styles.cardContent}>
            {props.childern}
          </Card.Content>
        </ScrollView>
      </Card>

      {props.uri !== undefined && (
        <Modal
          visible={isImagePreviewVisible}
          transparent={true}
          animationType="fade"
          onRequestClose={() => setIsImagePreviewVisible(false)}
        >
          <TouchableWithoutFeedback
            onPress={() => setIsImagePreviewVisible(false)}
          >
            <View style={styles.previewOverlay}>
              <Image
                style={styles.previewImage}
                source={{ uri: props.uri }}
                resizeMode="contain"
              />
              <IconButton
                icon="close"
                color="#FFFFFF"
                size={28}
                style={styles.previewCloseButton}
                onPress={() => setIsImagePreviewVisible(false)}
              />
            </View>
          </TouchableWithoutFeedback>
        </Modal>
      )}
    </View>
  );
};

const createStyles = (colors: any) =>
  StyleSheet.create({
    cardViewContainer: {
      flexDirection: "row",
      backgroundColor: "transparent",
    },
    cardContainer: {
      width: "100%",
      borderColor: colors.border,
      borderWidth: 1,
      borderRadius: 8,
      backgroundColor: colors.surface,
      alignItems: "center",
      justifyContent: "flex-start",
      flex: 1,
    },
    cardImage: {
      resizeMode: "contain",
      width: "100%",
      height: 280,
      alignSelf: "center",
    },
    spinnerContainer: {
      width: "100%",
      height: 280,
      justifyContent: "center",
      alignItems: "center",
    },
    previewOverlay: {
      flex: 1,
      backgroundColor: "rgba(0, 0, 0, 0.9)",
      justifyContent: "center",
      alignItems: "center",
    },
    previewImage: {
      width: "90%",
      height: "80%",
    },
    previewCloseButton: {
      position: "absolute",
      top: 24,
      right: 24,
    },
    cardTitle: { width: "100%", marginTop: "2%" },
    cardContent: {
      marginBottom: 10,
      width: "100%",
      marginTop: 10,
      paddingHorizontal: 4,
    },
  });
